import { useCallback } from "react";
import { Link, useParams } from "react-router";
import { useTranslation } from "react-i18next";
import { getExtraction } from "../../api/knowledgeData/knowledgeDataApi";
import { readBatch } from "../../api/threads/batchesApi";
import { appPaths } from "../../shared/routing/appPaths";
import { useLoad } from "../../shared/loading/useLoad";
import ABoxView from "../knowledgeData/abox/ABoxView";
import { assembledVita, assembleResults } from "../knowledgeData/abox/aboxViewModel";
import LoadStatus from "../../ui/feedback/LoadStatus";
import { useTabTitle } from "../../shell/workspace";

export default function BatchVitaPage() {
  const { batchId = "", vita = "" } = useParams();
  const { t } = useTranslation();
  const load = useCallback(async (signal: AbortSignal) => {
    const batch = await readBatch(batchId, signal);
    const manifest = batch.assemblies.find((assembly) => assembly.vita === vita);
    if (manifest === undefined) throw new Error(t("batches.vitaNotFound"));
    const results = await Promise.all(manifest.partOutputs.map((source) => getExtraction(source, signal)));
    const assembled = assembleResults(manifest, results);
    return { manifest, results, assembled, shown: assembledVita(vita, results, assembled.statements) };
  }, [batchId, vita, t]);
  const { state, reload } = useLoad(load);
  useTabTitle(vita === "" ? null : { kind: "other", name: vita });
  return <div className="mx-auto max-w-5xl space-y-4 py-4">
    <h2 className="app-title break-all text-xl font-bold">{vita}</h2>
    <LoadStatus state={state} loadingLabel={t("common.loading")} onRetry={reload} />
    {state.status === "loaded" ? <>
      {!state.value.assembled.complete ? <p role="status" className="app-card p-3">{t("batches.partial")}: {t("batches.missingUnits")} {state.value.assembled.missingUnits.join(", ")}</p> : null}
      <ul>{state.value.results.map((result) => <li key={result.source}><Link className="app-link underline" to={appPaths.extraction(result.threadId, result.outputEntry)}>{result.source}</Link></li>)}</ul>
      <ABoxView extraction={state.value.shown} statementParts={(number) => <div><span className="app-text-muted text-xs">{t("batches.statementParts")}</span><ul className="flex flex-wrap gap-2 text-xs">{state.value.assembled.membership[number - 1]?.map((source) => {
        const result = state.value.results.find((result) => result.source === source);
        return result === undefined ? null : <li key={source}><Link className="app-link underline" to={appPaths.extraction(result.threadId, result.outputEntry)}>{source}</Link></li>;
      })}</ul></div>} />
    </> : null}
  </div>;
}
